// @ts-strict-ignore
import React, { Fragment, useMemo } from 'react';
import pluralize from 'pluralize';
import {
  Badge, Box, Separator, Text,
} from '@usealloy/component-library';
import { capitalize } from '~/analytics/utils/helpers';
import { Link, ScrollableStyledBox } from '~/entities/evaluations/watchlist-review/styles';
import { formatTimestamp } from '~/helpers/formatting';
import { normalToSnake, uniqueKeyGenerator } from '../helpers';
import { MatchDetailCard } from '../MatchDetailCard';
import { MatchDatum } from '../MatchDatum';
import { matchStyle } from '../styles';
import type { EntityData, EntityPiiData, MatchData } from '../../../types';

type SupportedMatchDetailsSummaryProps = {
  match: MatchData;
  entity: EntityData;
  entityPii: EntityPiiData;
};

const checkIfNameMatches = (entity, label: string, values: string[]) => label.toLocaleLowerCase().includes('name')
    && values.join(', ').toLocaleLowerCase() === entity.entity_name?.toLocaleLowerCase();

const checkIfDateMatches = (entityPii, label: string, values: string[]): boolean => {
  if (label.toLocaleLowerCase().includes('of birth')) {
    // remove day suffix because Date considers date invalid
    const monthDaysSuffixes = ['st', 'nd', 'rd', 'th'];
    const daySuffixToRemove = monthDaysSuffixes.find(
      sfx => entityPii?.dateOfBirth?.includes(sfx),
    );

    const formatDate = entityPii?.dateOfBirth?.split(daySuffixToRemove).join(' ');
    const entityDate = new Date(formatDate);
    const entityTimeStamp = entityDate.getTime();

    if (label.toLocaleLowerCase().includes('(beginning, ending)')) {
      const beginningDate = new Date(`${values[0]}T00:00:00`);
      const beginningTimeStamp = beginningDate.getTime();
      const endingDate = new Date(`${values[1]}T00:00:00`);
      const endingTimeStamp = endingDate.getTime();
      return entityTimeStamp >= beginningTimeStamp && entityTimeStamp <= endingTimeStamp;
    }

    let includesDate = false;
    values.forEach(date => {
      const valueDate = new Date(`${date}T00:00:00`);
      const valueTimeStamp = valueDate.getTime();
      if (valueTimeStamp === entityTimeStamp) {
        includesDate = true;
      }
    });

    return includesDate;
  }
  return false;
};

const SupportedMatchDetailsSummary = ({
  match,
  entity,
  entityPii,
}: SupportedMatchDetailsSummaryProps) => {
  const categorizedListings = useMemo(() => match.listings.reduce((acc, listing) => {
    if (!acc[listing.match_type]) {
      acc[listing.match_type] = { listings: [], totalMedia: 0 };
    }
    if (listing.media) {
      acc[listing.match_type].totalMedia += listing.media.length;
    }
    const newListing = {
      ...listing,
      reactKey: uniqueKeyGenerator.generate(listing.match_type),
      media: (listing.media || []).map(media => ({
        ...media,
        reactKey: uniqueKeyGenerator.generate(media.title),
      })),
      details: (listing.details || []).map(detail => ({
        ...detail,
        reactKey: uniqueKeyGenerator.generate(detail.label),
      })),
    };
    acc[listing.match_type].listings.push(newListing);

    return acc;
  }, {}), [match.listings]);

  return (
    <ScrollableStyledBox data-watchlist-container="scroller" data-testid="supportedMatchDetailSummary">
      {
        match?.match_types?.map(({ label }, idx) => {
          if (!categorizedListings[label]) {
            return null;
          }

          const { listings, totalMedia } = categorizedListings[label];

          return (
            <MatchDetailCard
              // eslint-disable-next-line react/no-array-index-key
              key={`${label}-${idx}`}
              data-watchlist-section={normalToSnake(label)}
              header={(
                <Box
                  style={{
                    border: 'none',
                    borderRadius: 'var(--cl-radius-3)',
                  }}
                  wrap="wrap"
                  justifyContent="space-between"
                  data-dd-action-name={`alloydd-watchlistDetail-sectionToggle-${label}`}
                >
                  <Badge style={{ background: 'none', padding: 0 }}>
                    <Box id={`match-details-label-${label}`} gap={2} justifyContent="flex-start">
                      <Text
                        lineHeight={4}
                        size={3}
                        weight={700}
                      >
                        {label}
                      </Text>
                    </Box>
                  </Badge>
                  <Text>
                    {`${listings.length} ${pluralize('Listing', listings.length)}${totalMedia ? ` With ${totalMedia} ${pluralize('Link', totalMedia)}` : ''}`}
                  </Text>
                </Box>
                )}
            >
              <Box direction="column" gap={4} key={label}>
                <Box direction="column" gap={4}>
                  {listings.map(({ reactKey: fragmentKey, details, media }, i) => (
                    <Fragment key={fragmentKey}>
                      {!!i && <Separator style={{ marginBlock: 'var(--cl-space-4)' }} />}
                      {!media.length && details.map(({
                        reactKey: detailKey,
                        label: detail,
                        values: detailValues,
                      }) => {
                        const matchesName = checkIfNameMatches(entity, detail, detailValues);
                        const matchesDate = checkIfDateMatches(entityPii, detail, detailValues);
                        const matchedStyle = matchesName || matchesDate ? matchStyle : {};
                        return (
                          <MatchDatum key={detailKey} label={capitalize(detail)}>
                            <Text
                              size={2}
                              lineHeight={3}
                              style={matchedStyle}
                            >
                              {detailValues.join(', ')}
                            </Text>
                          </MatchDatum>
                        );
                      })}
                      {media?.map((medium, j) => (
                        <Fragment key={medium.reactKey}>
                          {!!j && <Separator style={{ marginBlock: 'var(--cl-space-4)' }} />}
                          <MatchDatum label="Name">{match.name}</MatchDatum>
                          <MatchDatum label="Date">{formatTimestamp(medium.date)}</MatchDatum>
                          <MatchDatum label="Title">{medium.title}</MatchDatum>
                          <MatchDatum label="Snippet">{medium.snippet}</MatchDatum>
                          <MatchDatum label="URL">
                            <Link
                              as="a"
                              color="var(--cl-juicy-blue)"
                              href={medium.url}
                              rel="noopener noreferrer"
                              target="_blank"
                              data-dd-action-name="alloydd-watchlistDetail-url"
                            >
                              {medium.url}
                            </Link>
                          </MatchDatum>
                        </Fragment>
                      ))}
                    </Fragment>
                  ))}
                </Box>
              </Box>
            </MatchDetailCard>
          );
        })
      }
    </ScrollableStyledBox>
  );
};

export default SupportedMatchDetailsSummary;
